// Perfil del jugador: nombre + avatar (emoji o imagen), guardado en localStorage.
// Se usa en las partidas y en el multijugador.
const AVATARS = ['🙂', '😎', '🦊', '🐼', '🐸', '🦁', '🐵', '🐯', '🦉', '🐧', '🐙', '🦄', '🤖', '👾', '🧙', '🧑‍🚀', '🦕', '🐲'];

export class ProfileUI {
  constructor({ modals, progression, bus, onSaved }) {
    this.modals = modals;
    this.progression = progression;
    this.bus = bus;
    this.onSaved = onSaved;
  }

  /** ¿El jugador ya eligió su nombre alguna vez? */
  get isOnboarded() {
    return !!this.progression.profile.onboarded;
  }

  /** Abre el editor de perfil. firstTime=true en la primera vez (bienvenida). */
  open({ firstTime = false } = {}) {
    const p = this.progression.profile;
    // Estado temporal (no se guarda hasta pulsar Guardar).
    const sel = {
      name: firstTime && p.name === 'Constructor' ? '' : (p.name || ''),
      avatar: p.avatar && p.avatar !== 'default' ? p.avatar : '🙂',
      image: p.avatarImage || null, // data URL si el jugador subió una imagen
    };

    const statsHTML = firstTime ? '' : this._statsHTML(p);
    const body = `
      <p class="sub-hint">${firstTime ? '¡Bienvenido/a! Elige cómo quieres que te vean en el juego.' : 'Edita tu nombre y tu avatar.'}</p>
      <div class="prof-cols">
        <div class="prof-col prof-col-main">
          <div class="prof-preview">
            <div class="prof-ava" id="prof-ava"></div>
            <div class="prof-name-wrap">
              <label class="prof-label">Tu nombre</label>
              <input id="prof-name" class="code-input prof-input" maxlength="16" placeholder="Escribe tu nombre" />
            </div>
          </div>
          <label class="prof-label">Elige un avatar</label>
          <div class="ava-grid" id="ava-grid">
            ${AVATARS.map((a) => `<button class="ava-opt" data-ava="${a}">${a}</button>`).join('')}
          </div>
          <div class="prof-upload">
            <button class="prof-upbtn prof-upbtn-add" id="prof-upload-btn">Subir una imagen</button>
            <button class="prof-upbtn prof-upbtn-remove hidden" id="prof-clear-btn">Quitar imagen</button>
            <input type="file" id="prof-file" accept="image/*" class="hidden" />
          </div>
        </div>
        ${statsHTML ? `<div class="prof-col prof-col-stats">${statsHTML}</div>` : ''}
      </div>`;

    const modal = this.modals.open({
      title: firstTime ? '¿CÓMO TE LLAMAS?' : 'TU PERFIL',
      className: 'mode-modal modes-modal profile-modal',
      dismissible: !firstTime,
      bodyHTML: body,
    });
    // Cancelar: círculo con X arriba a la derecha (solo al editar, no en el alta inicial).
    if (!firstTime) {
      const closeBtn = document.createElement('button');
      closeBtn.className = 'modal-close';
      closeBtn.setAttribute('aria-label', 'Cancelar');
      closeBtn.innerHTML = '✕';
      closeBtn.addEventListener('click', () => this.modals.close());
      modal.appendChild(closeBtn);
    }
    // Guardar: círculo verde abajo a la derecha con un check limpio (sin rebote).
    const saveBtn = document.createElement('button');
    saveBtn.className = firstTime ? 'lobby-start' : 'lobby-start ls-save';
    saveBtn.setAttribute('aria-label', firstTime ? 'Empezar' : 'Guardar');
    saveBtn.innerHTML = firstTime
      ? '▶'
      : '<svg class="ls-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
    saveBtn.addEventListener('click', () => { this._save(sel, modal); this.modals.close(); });
    modal.appendChild(saveBtn);

    const nameInput = modal.querySelector('#prof-name');
    nameInput.value = sel.name;
    nameInput.addEventListener('input', () => { sel.name = nameInput.value; });

    const renderAva = () => {
      const el = modal.querySelector('#prof-ava');
      el.innerHTML = sel.image ? `<img src="${sel.image}" alt="avatar" />` : `<span>${sel.avatar}</span>`;
      modal.querySelectorAll('.ava-opt').forEach((b) => b.classList.toggle('sel', !sel.image && b.getAttribute('data-ava') === sel.avatar));
      modal.querySelector('#prof-clear-btn').classList.toggle('hidden', !sel.image);
    };
    renderAva();

    modal.querySelectorAll('.ava-opt').forEach((b) => b.addEventListener('click', () => {
      sel.avatar = b.getAttribute('data-ava'); sel.image = null; renderAva();
      this.bus.emit('audio:sfx', { name: 'pick' });
    }));

    const fileInput = modal.querySelector('#prof-file');
    modal.querySelector('#prof-upload-btn').addEventListener('click', () => fileInput.click());
    modal.querySelector('#prof-clear-btn').addEventListener('click', () => { sel.image = null; renderAva(); });
    fileInput.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      this._readImageScaled(file, 128).then((dataUrl) => { sel.image = dataUrl; renderAva(); }).catch(() => {});
    });

    // No enfocamos el nombre automáticamente: evita que se abra el teclado solo en móvil.
  }

  /** Ficha de estadísticas del jugador. */
  _statsHTML(p) {
    const s = p.stats || {};
    const completed = s.gamesPlayed || 0;      // partidas terminadas (ganadas + perdidas)
    const wins = s.wins || 0;
    const abandoned = s.abandoned || 0;
    const losses = Math.max(0, completed - wins);
    // Total = ganadas + perdidas + abandonadas. El % de victorias REAL cuenta también las abandonadas.
    const total = wins + losses + abandoned;
    const pct = total ? Math.round((wins / total) * 100) : 0;
    const bw = s.bestWord || { word: '', points: 0 };
    const longest = (s.longestWord || '—').toUpperCase();
    const best = bw.word ? `${bw.word.toUpperCase()}` : '—';
    return `
      <div class="prof-stats-title">📊 Tus estadísticas</div>
      <div class="stat-grid">
        <div class="stat-cell"><span class="stat-val">${total}</span><span class="stat-label">Partidas</span></div>
        <div class="stat-cell"><span class="stat-val">${wins}</span><span class="stat-label">Ganadas</span></div>
        <div class="stat-cell"><span class="stat-val">${losses}</span><span class="stat-label">Perdidas</span></div>
        <div class="stat-cell"><span class="stat-val">${abandoned}</span><span class="stat-label">Abandonadas</span></div>
        <div class="stat-cell"><span class="stat-val">${pct}%</span><span class="stat-label">Victorias</span></div>
        <div class="stat-cell"><span class="stat-val">${s.wordsCreated || 0}</span><span class="stat-label">Palabras</span></div>
        <div class="stat-cell"><span class="stat-val stat-word">${longest}</span><span class="stat-label">Más larga</span></div>
        <div class="stat-cell"><span class="stat-val stat-word">${best}</span><span class="stat-label">Mejor (${bw.points || 0} pts)</span></div>
      </div>`;
  }

  _save(sel, modal) {
    const name = (sel.name || '').trim().slice(0, 16) || 'Jugador';
    const p = this.progression.profile;
    p.name = name;
    p.nameCustom = true;   // el jugador fijó su nombre a mano: ya no se sobrescribe con el de Google
    p.avatar = sel.image ? 'custom' : sel.avatar;
    p.avatarImage = sel.image || null;
    p.avatarCustom = true; // idem para el avatar elegido
    p.onboarded = true;
    this.progression.save();
    this.bus.emit('audio:sfx', { name: 'button' });
    this.onSaved?.();
  }

  /** Lee una imagen y la reescala a un cuadrado de `size` px (data URL webp/png pequeño). */
  _readImageScaled(file, size) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = size; c.height = size;
          const ctx = c.getContext('2d');
          // recorte cuadrado centrado
          const s = Math.min(img.width, img.height);
          const sx = (img.width - s) / 2, sy = (img.height - s) / 2;
          ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size);
          resolve(c.toDataURL('image/webp', 0.85));
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

/** Devuelve HTML para pintar un avatar (emoji o imagen). */
export function avatarHTML(player) {
  const av = player?.avatar;
  const img = player?.avatarImage;
  // 'custom' sin imagen NO es un emoji: cae al icono por defecto (🤖 IA / 🙂 humano).
  const emoji = av && av !== 'default' && av !== 'custom' ? av : (player?.isAI ? '🤖' : '🙂');
  if (img && (av === 'custom' || String(img).startsWith('data:'))) {
    // Si la imagen no carga (p. ej. URL de Google sin internet), se sustituye por el emoji
    // en vez de dejar el cuadro de imagen rota.
    return `<img class="ava-img" src="${img}" alt="" onerror="this.outerHTML='<span>${player?.isAI ? '🤖' : '🙂'}</span>'" />`;
  }
  return `<span>${emoji}</span>`;
}
